import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dismissForSession, markCompleted } from '../../services/tutorialService';
import './TutorialTour.css';

// Etapes de decouverte R25 : la navigation se fait par les TUILES du
// dashboard (data-tour). Les etapes restent sur /dashboard et mettent
// en avant chaque tuile, puis viennent les regles.
const STEPS = [
  {
    key: 'welcome',
    title: 'Bienvenue dans Novarcane ! ⚡',
    target: null,
    body: "Je vais te faire visiter l'application et t'apprendre les bases du duel. Compte tuto ou invité : ce tutoriel ne lancera qu'une seule fois. Prêt ?",
  },
  {
    key: 'collection',
    title: 'Collection & Decks',
    route: '/dashboard',
    target: 'nav-collection',
    body: "Toutes tes cartes classées par élément, ET tes decks : deux onglets dans la même page. Un deck complet (20 cartes) est obligatoire pour jouer en ligne.",
  },
  {
    key: 'duel',
    title: 'Duel',
    route: '/dashboard',
    target: 'nav-play',
    body: "Crée ou rejoins une partie en ligne, ou affronte l'IA. C'est aussi ici que tu pourras relancer ce tutoriel à tout moment.",
  },
  {
    key: 'shop',
    title: 'Boutique',
    route: '/dashboard',
    target: 'nav-shop',
    body: "Dépense tes pièces en paquets de cartes et cosmétiques (dos de cartes). Des offres quotidiennes t'attendent.",
  },
  {
    key: 'rankings',
    title: 'Classement',
    route: '/dashboard',
    target: 'nav-rankings',
    body: "Les meilleurs duellistes de Novarcane. Ta place s'améliore à chaque victoire.",
  },
  {
    key: 'profile',
    title: 'Ton profil',
    route: '/dashboard',
    target: 'nav-profile',
    body: "En haut à gauche : ton badge de duelliste. Touche-le pour ouvrir ton profil (stats, réglages, déconnexion). Invité ? Il te mènera aussi à la connexion.",
  },
  {
    key: 'rules-1',
    title: 'Les règles — l\'énergie ⚡',
    target: null,
    body: "Chaque tour, tu gagnes de l'énergie (max 10). Chaque carte a un coût : invoquer un monstre, lancer un sort... tout se paie en énergie. Joue les cartes coûteuses quand ton énergie le permet !",
  },
  {
    key: 'rules-2',
    title: 'Les règles — invocation',
    target: null,
    body: "Glisse une carte de ta MAIN vers la ZONE DES MONSTRES (6 emplacements). Astuce : dépose-la exactement où tu veux la voir — à gauche, à droite ou ENTRE deux cartes : c'est toi qui choisis sa position !",
  },
  {
    key: 'rules-3',
    title: 'Les règles — attaque & défense',
    target: null,
    body: "Glisse un monstre vers la ZONE DE COMBAT pour le déclarer attaquant (là aussi, choisis sa place au doigt). Si l'adversaire attaque, glisse un de tes monstres SUR son attaquant pour le bloquer. Les monstres non bloqués frappent directement les PV !",
  },
  {
    key: 'rules-4',
    title: 'Les règles — l\'objectif',
    target: null,
    body: "Réduis les points de vie de ton adversaire à zéro. Combine invocations, sorts, pièges (pose cachée) et terrains pour prendre l'avantage. Utilise la pierre-papier-ciseaux du début pour choisir qui commence !",
  },
];

export default function TutorialTour({ autoLaunch = false }) {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [checked, setChecked] = useState(false);
  const stepRef = useRef(null);
  stepRef.current = null;

  // Lancement : auto (premiere connexion) ou relancement manuel (event global)
  useEffect(() => {
    const launch = () => { setVisible(true); setStepIndex(0); };
    window.addEventListener('elementum:tutorial-start', launch);
    if (autoLaunch) launch();
    else if (window.__elementumTutorialPending) {
      window.__elementumTutorialPending = false;
      launch();
    }
    return () => window.removeEventListener('elementum:tutorial-start', launch);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setChecked(true); }, []);

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;
  stepRef.current = step;

  // Navigation vers la page de l'etape
  useEffect(() => {
    if (!visible || !step) return;
    if (step.route) navigate(step.route);
  }, [visible, stepIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = async (launchDuel) => {
    setVisible(false);
    await markCompleted();
    if (launchDuel) {
      navigate('/game/matchmaking?tutorial=1');
    } else {
      dismissForSession();
      navigate('/dashboard');
    }
  };

  if (!checked || !visible || !step) return null;

  return (
    <div className="tutorial-tour-overlay" role="dialog" aria-label="Tutoriel">
      <TargetHighlight targetKey={step.target} />
      <div className="tutorial-card">
        <div className="tutorial-progress">
          {STEPS.map((s, i) => (
            <span key={s.key} className={`tutorial-dot ${i === stepIndex ? 'active' : ''} ${i < stepIndex ? 'done' : ''}`} />
          ))}
        </div>
        <h2 className="tutorial-title">{step.title}</h2>
        <p className="tutorial-body">{step.body}</p>
        <div className="tutorial-actions">
          <button
            type="button"
            className="tutorial-btn tutorial-btn--ghost"
            onClick={() => finish(false)}
          >
            Passer
          </button>
          {stepIndex > 0 ? (
            <button
              type="button"
              className="tutorial-btn tutorial-btn--ghost"
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            >
              Retour
            </button>
          ) : null}
          {isLast ? (
            <>
              <button
                type="button"
                className="tutorial-btn tutorial-btn--ghost"
                onClick={() => finish(false)}
              >
                Terminer
              </button>
              <button
                type="button"
                className="tutorial-btn tutorial-btn--primary"
                onClick={() => finish(true)}
              >
                ⚔️ Lancer le duel tutoriel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="tutorial-btn tutorial-btn--primary"
              onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))}
            >
              Suivant →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Spotlight de la cible [data-tour=...] : affiche et suit l'element,
// uniquement s'il est reellement visible a l'ecran.
function TargetHighlight({ targetKey }) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!targetKey) { setRect(null); return undefined; }
    let raf;
    const measure = () => {
      const el = document.querySelector(`[data-tour="${targetKey}"]`);
      if (el && el.offsetParent !== null) {
        const r = el.getBoundingClientRect();
        setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
      raf = requestAnimationFrame(measure);
    };
    measure();
    return () => cancelAnimationFrame(raf);
  }, [targetKey]);

  if (!rect) return null;
  return (
    <div
      className="tutorial-spotlight"
      style={{
        left: rect.left - 8,
        top: rect.top - 8,
        width: rect.width + 16,
        height: rect.height + 16,
      }}
    />
  );
}
